import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthApiError, getAuthClient } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const presetToken = params.get('token') ?? '';
  const [token, setToken] = useState(presetToken);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast.error('비밀번호 확인이 일치하지 않습니다');
      return;
    }
    setSubmitting(true);
    try {
      await getAuthClient().resetPassword(token, password);
      toast.success('비밀번호가 재설정됐습니다. 다시 로그인하세요.');
      navigate('/login', { replace: true });
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>비밀번호 재설정</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            {!presetToken && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="token">재설정 토큰</Label>
                <Input
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">새 비밀번호 (10자, 3종 이상)</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password2">새 비밀번호 확인</Label>
              <Input
                id="password2"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <Button type="submit" disabled={submitting || !token}>
              {submitting ? '처리 중...' : '비밀번호 재설정'}
            </Button>
            <Link
              to="/login"
              className="text-center text-sm text-muted-foreground hover:underline"
            >
              로그인으로 돌아가기
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
